import React, { useEffect, useState, useCallback, Fragment } from 'react';

import useBundleTranslation from 'i18n';
import {
    compressTiles,
    FolderItem,
    HierarchyFolder,
    TileInfo,
    TopicsPopupProps,
    HomePageFilterState,
    HomePageState,
    FolderMapInterface,
} from 'app/home/index.functions';
import {
    processSections,
    DEFAULT_DISPLAY_ORDER,
    changeTilesOrder,
    categorySort,
    TileSection,
} from './TilePanel.functions';
import styles from './TilePanel.styles';
import TileComponent from './TileComponent';
import { Tile4FavoriteData } from 'app/home/index';
import { useThumbnailLoader } from 'app/home/useThumbnailLoader';
import { useHomeContext } from 'app/home/context';
import { Box, Stack, Divider, Alert, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import IconMi from 'components/common/icon/IconMi';
import { useSelector } from 'react-redux';
import { TFunction } from 'i18next';
import striptags from 'striptags';
import { SxProps } from '@mui/system/styleFunctionSx';
import { sanitizeHtml } from 'tools/safeHtml';
import { fillThemeSystemMessage } from 'tools/themeSystemMessage';

export default function TilePanel({
    tiles,
    setTile4Favorite,
    customFieldsDisplayOnTileInfo,
}: {
    tiles: TileInfo[];
    setTile4Favorite: (data: Tile4FavoriteData | null) => void;
    customFieldsDisplayOnTileInfo: { [key: string]: string };
}) {
    const { t } = useBundleTranslation(['app/home']);
    let thumbnailLoader = useThumbnailLoader();
    const [panelWidth, setPanelWidth] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const ctx = useHomeContext();
    const [
        homePageState,
        setHomePageState,
        tags,
        settings,
        contentTypes,
        hierarchyFolders,
        folderItems,
        vocabulary,
        favorites,
        folders,
        categories,
        folderMap,
        setTilePreview,
        setVisibleTiles,
        tilesSectionsRef,
        refetchData,
        trashBucketRef,
        isEmbedded,
        topicPopupProps,
    ] = [
        ctx.homePageState,
        ctx.setHomePageState,
        ctx.data.tags,
        ctx.homePageSettings,
        ctx.data.contentTypes,
        ctx.data.hierarchyFolders,
        ctx.data.folderItems,
        ctx.data.data.vocabulary,
        ctx.favorites,
        ctx.folders,
        ctx.categories,
        ctx.folderMap,
        ctx.setTilePreview,
        ctx.setVisibleTiles,
        ctx.tilesSectionsRef,
        ctx.refetchData,
        ctx.trashBucketRef,
        ctx.isEmbedded,
        ctx.topicPopupProps,
    ];

    let tilesSections = processSections(
        tiles,
        hierarchyFolders,
        favorites,
        folders,
        categories,
        tags,
        folderItems,
        contentTypes,
        settings,
        folderMap,
        homePageState,
        t,
        isEmbedded,
    );

    useEffect(() => {
        if (JSON.stringify(tilesSectionsRef.current) != JSON.stringify(tilesSections)) {
            tilesSectionsRef.current = tilesSections;
        }
    }, [tilesSections]);

    const multiLisColumnCount = settings?.theme?.catalog_list_column_count ?? 1;
    const isListView = 'list' === homePageState?.filterState?.view;
    const isMultiListView = isListView && multiLisColumnCount > 1;

    const getViewType = () => {
        if (isMultiListView) return 'multi-list';
        if (isListView) return 'list';
        return 'tile';
    };
    const viewType = getViewType();

    let panelInnerHeight = 0;
    const setFilterState = (filterState: Partial<HomePageFilterState>) => {
        setHomePageState({ ...homePageState, filterState: { ...(homePageState?.filterState ?? {}), ...filterState } });
        return true;
    };

    const supportAdmins = useSelector((state: any) => state.auth?.userInfo?.supportAdmins) ?? [];
    const brandThemeProperties = useSelector((state: any) => state.auth?.userInfo?.brandThemeProperties) ?? {};

    useEffect(() => {
        if (
            (0 === tilesSections.length || (1 == tilesSections.length && 0 == tilesSections[0].tiles.length)) &&
            undefined !== isEmbedded &&
            !isEmbedded &&
            homePageState?.filterState?.keywordFilter > '' &&
            'keywordFilter' === homePageState?.filterState?.lastFilterApplied &&
            homePageState?.filterState?.folderSelected != ''
        ) {
            setFilterState({ folderSelected: '', lastFilterApplied: 'folderSelected' });
        }
    }, [homePageState?.filterState]);

    const onTileClick = (tile: TileInfo | null) => {
        setTilePreview(tile);
        setVisibleTiles(tilesSections.flatMap((s) => s.tiles));
    };
    const appTheme = useTheme();

    const updatePanelWidth = () => {
        //update width for rerender position of tiles
        const rightPanelElement = document.getElementById('rightPanel');
        if (rightPanelElement) {
            setPanelWidth(rightPanelElement.getBoundingClientRect().width);
        }
    };

    useEffect(() => {
        const rightPanelElement = document.getElementById('rightPanel');
        updatePanelWidth();

        let resizeObserver: ResizeObserver | null = null;

        if (rightPanelElement) {
            resizeObserver = new ResizeObserver(() => {
                updatePanelWidth();
            });
            resizeObserver.observe(rightPanelElement);
        }

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            thumbnailLoader.clearImagesQueue();
        };
    }, []);

    const moveTile = useCallback((sectionKey: string, udei_id: string, hover_udei_id: string) => {
        const tileSection = tilesSections.find((s) => s.k == sectionKey);

        if (!tileSection) return;

        const dragTile = tileSection.tiles.find((t) => t.user_dashboard_element_instance_id == udei_id);
        const dropTile = tileSection.tiles.find((t) => t.user_dashboard_element_instance_id == hover_udei_id);

        let dragDisplayOrder = 0;
        let dropDisplayOrder = 0;
        let next_udei_id = '';
        let next_element_id = 0;
        let next_segment_value_id = 0;
        let favorite_id = 0;
        let category_id = 0;
        let folder_id = 0;

        if (dragTile) {
            if (sectionKey.startsWith('favorite_') || sectionKey.startsWith('category_')) {
                favorite_id = parseInt(sectionKey.replace('favorite_', ''));
                category_id = parseInt(sectionKey.replace('category_', ''));

                let sort_order_modified_by_owner_ind = 'N';
                let newHierarchyFolders: HierarchyFolder[] = hierarchyFolders.map((f) => {
                    if (
                        (favorite_id > 0 && favorite_id == f.favorite_id) ||
                        (category_id != 0 && '' + category_id == f.category_id)
                    ) {
                        sort_order_modified_by_owner_ind = f.sort_order_modified_by_owner_ind ?? 'N';
                        f.sort_order_modified_by_owner_ind = 'Y';
                    }

                    return f;
                });

                let doMap: { [key: string]: number } = {};
                if (category_id != 0 || 'N' === sort_order_modified_by_owner_ind) {
                    let filteredTiles = tiles.filter((t) => {
                        if (
                            (favorite_id > 0 && '' + favorite_id == t.favorite_id) ||
                            (category_id != 0 && '' + category_id == t.category_id)
                        ) {
                            return true;
                        }
                    });

                    filteredTiles = filteredTiles.sort(categorySort);
                    filteredTiles.map((t, i) => {
                        doMap[t.user_dashboard_element_instance_id ?? ''] = i + 1;
                    });
                }

                let newTiles: TileInfo[] = tiles.map((t, i) => {
                    if (
                        (favorite_id > 0 && '' + favorite_id == t.favorite_id) ||
                        (category_id != 0 && '' + category_id == t.category_id)
                    ) {
                        let iterator = doMap[t.user_dashboard_element_instance_id ?? ''] ?? i;
                        if (category_id != 0 || 'N' === sort_order_modified_by_owner_ind) t.display_order = iterator;

                        if (t.user_dashboard_element_instance_id == udei_id)
                            dragDisplayOrder = t.display_order ?? iterator;
                        if (t.user_dashboard_element_instance_id == hover_udei_id)
                            dropDisplayOrder = t.display_order ?? iterator;
                    }
                    return t;
                });

                let nextDisplayOrder =
                    dropDisplayOrder == dragDisplayOrder + 1 ? dropDisplayOrder + 1 : dropDisplayOrder;

                newTiles = newTiles.map((t) => {
                    if (
                        (favorite_id > 0 && '' + favorite_id == t.favorite_id) ||
                        (category_id != 0 && '' + category_id == t.category_id)
                    ) {
                        if (t.display_order == nextDisplayOrder)
                            next_udei_id = t.user_dashboard_element_instance_id ?? '';

                        if (t.display_order == dragDisplayOrder) {
                            t.display_order = dropDisplayOrder;
                        } else if (dragDisplayOrder < dropDisplayOrder) {
                            if (
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) > dragDisplayOrder &&
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) < dropDisplayOrder
                            )
                                t.display_order = (t.display_order ?? DEFAULT_DISPLAY_ORDER) - 1;

                            if ((t.display_order ?? DEFAULT_DISPLAY_ORDER) == dropDisplayOrder)
                                t.display_order =
                                    (t.display_order ?? DEFAULT_DISPLAY_ORDER) +
                                    (dropDisplayOrder == dragDisplayOrder + 1 ? -1 : 1);
                        } else {
                            if (
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) >= dropDisplayOrder &&
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) < dragDisplayOrder
                            )
                                t.display_order = (t.display_order ?? DEFAULT_DISPLAY_ORDER) + 1;
                        }
                    }

                    return t;
                });

                let rows = compressTiles(newTiles, vocabulary);

                ctx.setData({ ...ctx.data, hierarchyFolders: newHierarchyFolders, data: { ...ctx.data.data, rows } });
            } else if (sectionKey.startsWith('folder_')) {
                folder_id = parseInt(sectionKey.replace('folder_', ''));
                let dragFolderItem: FolderItem | null =
                    folderItems.find(
                        (t) =>
                            t.folder_id == folder_id &&
                            t.element_id == dragTile.element_id &&
                            t.segment_value_id == dragTile.segment_value_id,
                    ) ?? null;

                if (dragFolderItem) dragDisplayOrder = dragFolderItem.display_order ?? 0;
                if (dropTile) {
                    let dropFolderItem: FolderItem | null =
                        folderItems.find(
                            (t) =>
                                t.folder_id == folder_id &&
                                t.element_id == dropTile.element_id &&
                                t.segment_value_id == dropTile.segment_value_id,
                        ) ?? null;

                    if (dropFolderItem) dropDisplayOrder = dropFolderItem.display_order ?? 0;
                }

                let nextDisplayOrder =
                    dropDisplayOrder == dragDisplayOrder + 1 ? dropDisplayOrder + 1 : dropDisplayOrder;

                const newFolderItems: FolderItem[] = folderItems.map((t) => {
                    if (folder_id == t.folder_id) {
                        if (t.display_order == nextDisplayOrder) {
                            next_element_id = t.element_id;
                            next_segment_value_id = t.segment_value_id;
                        }

                        if (t.display_order == dragDisplayOrder) {
                            t.display_order = dropDisplayOrder;
                        } else if (dragDisplayOrder < dropDisplayOrder) {
                            if (
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) > dragDisplayOrder &&
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) < dropDisplayOrder
                            )
                                t.display_order = (t.display_order ?? DEFAULT_DISPLAY_ORDER) - 1;

                            if ((t.display_order ?? DEFAULT_DISPLAY_ORDER) == dropDisplayOrder)
                                t.display_order =
                                    (t.display_order ?? DEFAULT_DISPLAY_ORDER) +
                                    (dropDisplayOrder == dragDisplayOrder + 1 ? -1 : 1);
                        } else {
                            if (
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) >= dropDisplayOrder &&
                                (t.display_order ?? DEFAULT_DISPLAY_ORDER) < dragDisplayOrder
                            )
                                t.display_order = (t.display_order ?? DEFAULT_DISPLAY_ORDER) + 1;
                        }
                    }

                    return t;
                });

                if (next_element_id > 0) {
                    const nextTile = tilesSections
                        .find((s) => s.k == sectionKey)
                        ?.tiles.find(
                            (t) => t.element_id == next_element_id && t.segment_value_id == next_segment_value_id,
                        );

                    if (nextTile) next_udei_id = nextTile.user_dashboard_element_instance_id ?? '';
                }

                let newHierarchyFolders: HierarchyFolder[] = hierarchyFolders.map((f) => {
                    if (f.folder_id == folder_id) f.sort_order_modified_by_owner_ind = 'Y';

                    return f;
                });

                ctx.setData({ ...ctx.data, hierarchyFolders: newHierarchyFolders, folderItems: newFolderItems });
            }

            if (category_id != 0) {
                changeTilesOrder(category_id, favorite_id, folder_id, udei_id, next_udei_id).then(() => {
                    refetchData();
                });
            } else changeTilesOrder(category_id, favorite_id, folder_id, udei_id, next_udei_id);
        }
    }, []);

    const getEmptyMessage = useCallback(
        (homePageState: HomePageState, t: TFunction, folderMap: FolderMapInterface): string => {
            let emptyText = '';
            let categorySelected = '';

            let hasFilter =
                homePageState?.filterState?.segmentFilter > '' || homePageState?.filterState?.tagFilter?.length > 0;
            let criteriaText = hasFilter ? t('empty_text.criteria') : '';

            if (homePageState?.filterState?.folderSelected > '') {
                if (undefined !== folderMap[homePageState.filterState.folderSelected])
                    categorySelected = striptags(folderMap[homePageState.filterState.folderSelected].name, ['b', 'i']);

                if (
                    homePageState.filterState.folderSelected.startsWith('favorite_') ||
                    homePageState.filterState.folderSelected.startsWith('folder_')
                )
                    emptyText = t('empty_text.alert_dashboard_empty_text_1', {
                        categoryName: categorySelected,
                        criteria: criteriaText,
                    });
                else if (homePageState.filterState.folderSelected.startsWith('category_'))
                    emptyText = t('empty_text.alert_dashboard_empty_text_2', {
                        categoryName: categorySelected,
                        criteria: criteriaText,
                    });
                else if (!hasFilter && 'elements_i_own' == homePageState.filterState?.segmentFilter)
                    emptyText = t('empty_text.alert_dashboard_empty_text_3');
            }

            if ('' == emptyText) {
                if (hasFilter) emptyText = t('empty_text.alert_dashboard_empty_text_4');
                else emptyText = t('empty_text.alert_dashboard_empty_text_5');
            }

            return emptyText;
        },
        [homePageState?.filterState, folderMap],
    );

    const getJsx = () => {
        const containerElement = document.getElementById('rightPanel');
        const panelHeight = containerElement != null ? containerElement.getBoundingClientRect().height : 0;
        const panelWidth = containerElement != null ? containerElement.getBoundingClientRect().width : 0;

        panelInnerHeight = panelHeight - (isListView ? 24 : 0);
        let jsx: (React.JSX.Element | null)[] = [];
        if (isMultiListView) {
            let pushColumnJsx = (columnJsx: React.JSX.Element[], c: number) => {
                if (c > 1 && columnJsx.length > 0)
                    jsx.push(
                        <Divider
                            key={c + '_div_wrapper_column_divider'}
                            orientation="vertical"
                            className="tile-list-divider"
                        />,
                    );
                jsx.push(
                    <Box className="tile-list-section" key={c + '_div_wrapper_column'} id={'div_wrapper_column_' + c}>
                        {columnJsx}
                    </Box>,
                );
            };

            const isDisplayContinued = tilesSections.length > 1;
            const maximumTiles = settings?.theme?.catalog_list_maximum_tiles ?? 200;
            let tilesCount = 0;
            let itemsCount = 0;

            for (let i = 0; i < tilesSections.length; i++) {
                tilesCount += tilesSections[i].tiles.length;

                if (tilesCount > maximumTiles) {
                    itemsCount += 1 + (maximumTiles - (tilesCount - tilesSections[i].tiles.length));
                    break;
                } else {
                    itemsCount += 1 + tilesSections[i].tiles.length;
                }
            }

            if (0 === tilesCount) return jsx;

            const columnCount = multiLisColumnCount;

            const tilesPerColumn = Math.max(
                Math.floor((itemsCount + (isDisplayContinued ? columnCount - 1 : 0)) / columnCount) + 1,
                15,
            );

            let tilesCounter = 0;
            let itemsCounter = 0;
            let columnCounter = 0;
            let finished = false;
            let columnJsx: React.JSX.Element[] = [];

            let getSectionTitleJsx = (
                s: TileSection,
                sectionIndex: number,
                isContinued: boolean,
            ): React.JSX.Element => {
                const icons: any = { favorite: 'favorite', folder: 'folders', category: 'categories' };
                const iconTypeParams = s.k.split('_');
                const onSectionClick = () => {
                    if (undefined !== icons[iconTypeParams[0]]) {
                        setFilterState({
                            folderSelected: homePageState?.filterState?.folderSelected != s.k ? s.k : '',
                            lastFilterApplied: 'folderSelected',
                        });
                    }
                };

                itemsCounter++;

                return (
                    <Stack
                        className={`tile-list-section__title tile-list-section__title--${undefined !== icons[iconTypeParams[0]] ? 'clickable' : 'default'}`}
                        direction={'row'}
                        key={'div_section_title_' + sectionIndex}
                        onClick={onSectionClick}
                    >
                        {'Y' === settings.theme?.catalog_list_display_fav_fol_cat_icons && icons[iconTypeParams[0]] && (
                            <Box
                                className={'tile-list-section__title-icon'}
                                key={'div_section_title_icon_' + sectionIndex}
                            >
                                <IconMi icon={icons[iconTypeParams[0]]} />
                            </Box>
                        )}
                        <Box className={'tile-list-section__title-text'}>
                            {s.title}
                            {isContinued ? ' (' + t('continued') + ')' : ''}
                        </Box>
                    </Stack>
                );
            };

            tilesSections.map((s, sectionIndex: number) => {
                if (finished || 0 === s.tiles.length) return;

                if (itemsCounter >= tilesPerColumn - 1 || tilesCounter >= maximumTiles) {
                    columnCounter++;
                    itemsCounter = 0;
                    pushColumnJsx(columnJsx, columnCounter);
                    columnJsx = [];

                    if (columnCounter >= columnCount || tilesCounter >= maximumTiles) finished = true;
                }

                if (!finished) columnJsx.push(getSectionTitleJsx(s, sectionIndex, false));

                s.tiles.map((tile, k: number) => {
                    if (finished) return;

                    if (itemsCounter >= tilesPerColumn || tilesCounter >= maximumTiles) {
                        columnCounter++;
                        itemsCounter = 0;
                        pushColumnJsx(columnJsx, columnCounter);
                        columnJsx = [];

                        if (columnCounter >= columnCount || tilesCounter >= maximumTiles) {
                            finished = true;
                            return;
                        } else {
                            if (isDisplayContinued && k > 0) columnJsx.push(getSectionTitleJsx(s, sectionIndex, true));
                        }
                    }

                    columnJsx.push(
                        <TileComponent
                            index={k}
                            moveTile={moveTile}
                            key={tile.user_dashboard_element_instance_id + '_' + s.k + '_t_' + k}
                            tile={tile}
                            section={s}
                            thumbnailLoader={thumbnailLoader}
                            setTile4Favorite={setTile4Favorite}
                            customFieldsDisplayOnTileInfo={customFieldsDisplayOnTileInfo}
                            setTilePreview={(tile) => {
                                onTileClick(tile);
                            }}
                            view={viewType}
                        />,
                    );
                    itemsCounter++;
                    tilesCounter++;
                });
            });

            if (columnCounter < columnCount) {
                columnCounter++;
                pushColumnJsx(columnJsx, columnCounter);

                if (columnCounter < columnCount)
                    for (let zi = columnCounter + 1; zi <= columnCount; zi++) pushColumnJsx([], zi);
            }

            const maximumExceededMessage =
                tilesCount > maximumTiles &&
                undefined !== settings.theme?.catalog_list_maximum_exceeded_message &&
                settings.theme?.catalog_list_maximum_exceeded_message > '' ? (
                    <Alert
                        className={'multi-list-content__exceeded_message'}
                        key={'div_wrapper_column_exceeded_message'}
                        id={'div_wrapper_column_exceeded_message'}
                        severity={'info'}
                        variant={'standard'}
                        sx={{ justifyContent: 'center', mx: '-19px' }}
                    >
                        {settings.theme?.catalog_list_maximum_exceeded_message}
                    </Alert>
                ) : null;

            let multiListCssClasses = 'multi-list-content';
            if ('Y' === settings.theme?.catalog_list_display_lock_icon) {
                multiListCssClasses += ' always-show--lock';
            }
            if ('Y' === settings.theme?.catalog_list_display_certification_icon) {
                multiListCssClasses += ' always-show--certification';
            }
            if (jsx.length > 0)
                return [
                    <Fragment key={'jsx_div_wrapper'}>
                        <Stack direction={'row'} spacing={2} className={multiListCssClasses}>
                            {jsx}
                        </Stack>
                        {maximumExceededMessage}
                    </Fragment>,
                ];

            return jsx;
        }

        let titleHeight = isListView ? 34 : 61;
        let top = 0;
        let left = 0;
        let tileWidth = 0;
        let tileHeight = 0;

        if (!isListView) {
            tileWidth = Number(appTheme.componentSettings.tile.width) + Number(appTheme.componentSettings.tile.margin);
            tileHeight =
                Number(appTheme.componentSettings.tile.height) + Number(appTheme.componentSettings.tile.margin);
        } else tileHeight = Number(appTheme.componentSettings.tileList.height);

        tilesSections.map((s, sectionIndex: number) => {
            let isNewLine = false;
            let style = {
                top: top + 'px',
                left: '0px',
            };

            top += s.title > '' ? titleHeight : 0;
            left = 0;

            let tilesJsx: (React.JSX.Element | null)[] = [];

            s.tiles.map((tile, k: number) => {
                let style = {
                    top: top + 'px',
                    left: left + 'px',
                };

                isNewLine = false;
                if (tileWidth == 0) {
                    left = 0;
                    isNewLine = true;
                    top += tileHeight;
                } else {
                    left += tileWidth;

                    if (left + tileWidth > panelWidth) {
                        left = 0;
                        top += tileHeight;
                        isNewLine = true;
                    }
                }

                if (scrollTop - panelHeight < top && top < scrollTop + 2 * panelHeight) {
                    tilesJsx.push(
                        <TileComponent
                            index={k}
                            moveTile={moveTile}
                            key={tile.user_dashboard_element_instance_id + '_' + s.k + '_t_' + k}
                            tile={tile}
                            style={style}
                            section={s}
                            thumbnailLoader={thumbnailLoader}
                            setTile4Favorite={setTile4Favorite}
                            customFieldsDisplayOnTileInfo={customFieldsDisplayOnTileInfo}
                            setTilePreview={(tile) => {
                                onTileClick(tile);
                            }}
                            view={viewType}
                        />,
                    );
                }
            });

            if (s.tiles.length > 0) {
                jsx.push(
                    <TileListSection
                        key={`${s.k}_div_wrapper_${sectionIndex}`}
                        section={s}
                        tilesJsx={tilesJsx}
                        sx={style}
                    />,
                );
            }

            if (!isNewLine) top += tileHeight;
        });
        panelInnerHeight = top;
        return jsx;
    };

    if (trashBucketRef && trashBucketRef.current) trashBucketRef.current.style.display = 'none';
    let jsx = getJsx();
    const handleScroll = (event: any) => {
        setScrollTop(event.currentTarget.scrollTop);
    };

    const removePadding = isEmbedded ? true : false;
    const isFirstSectionUntitled = '' === tilesSections.find((s) => s.tiles.length > 0)?.title;

    const getThemeEmptyMessage = () => {
        const themeEmptyMessage = brandThemeProperties?.brand_theme_sys_msg_homepage_no_tiles_message ?? '';
        const searchQuery = homePageState?.filterState?.keywordFilter ?? '';
        if (searchQuery == '' || themeEmptyMessage == '') return '';

        return fillThemeSystemMessage(themeEmptyMessage, searchQuery, supportAdmins);
    };

    const themeEmptyMessageResult = getThemeEmptyMessage();

    return (
        <Box
            id={'tileView'}
            className={`tile-view-container tile-view-container--${viewType} tile-view-container--${isEmbedded ? 'embedded' : 'default'} ${removePadding ? 'tile-view-container--remove-padding' : ''} ${isFirstSectionUntitled ? 'tile-view-container--untitled' : ''}`}
            onScroll={handleScroll}
            //@ts-ignore
            sx={styles.tileViewContainer}
        >
            <>
                {0 == jsx.length && (
                    //@ts-ignore
                    <Stack
                        key={'no_empty_message'}
                        className={`empty-message-wrapper empty-message-wrapper--${themeEmptyMessageResult ? 'custom' : 'default'}`}
                        sx={styles.emptyMessageWrapper}
                    >
                        {themeEmptyMessageResult ? (
                            <>
                                <Box className={'empty-message-wrapper__custom-message-indent'} />
                                <Box className={'empty-message-wrapper__custom-message-holder'}>
                                    <Box
                                        className={'empty-message-wrapper__custom-message'}
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHtml(themeEmptyMessageResult),
                                        }}
                                        sx={styles.emptyMessageContent}
                                    />
                                </Box>
                                <Box className={'empty-message-wrapper__custom-message-indent'} />
                            </>
                        ) : (
                            <Box
                                className={'empty-message-wrapper__default-message default-empty-message'}
                                sx={styles.emptyMessageContent}
                            >
                                <Box className={'default-empty-message__icon-holder'}>
                                    <IconMi className={'default-empty-message__icon'} icon={'elements-2'} />
                                </Box>
                                <Box
                                    className={'default-empty-message__text'}
                                    dangerouslySetInnerHTML={{
                                        __html: getEmptyMessage(homePageState, t, folderMap),
                                    }}
                                ></Box>
                            </Box>
                        )}
                    </Stack>
                )}
                <Box
                    id={'tileViewInner'}
                    className={'tile-list-holder tile-view-container__tile-list-holder'}
                    sx={{
                        height: (0 == jsx.length ? '0' : panelInnerHeight) + 'px',
                    }}
                >
                    {jsx}
                </Box>
            </>
        </Box>
    );
}

function TileListSection({
    section,
    tilesJsx,
    sx,
}: {
    section: TileSection;
    tilesJsx: (React.JSX.Element | null)[];
    sx?: SxProps<Theme>;
}) {
    return (
        <Box className={'tile-list-section'}>
            {section.title > '' ? (
                <Box className={'tile-list-section__title'} sx={sx}>
                    <Box className={'tile-list-section__title-text'}>
                        <SectionTitle section={section} />
                    </Box>
                </Box>
            ) : null}
            <Box className={'tile-list-section__elements'}>{tilesJsx}</Box>
        </Box>
    );
}

function SectionTitle({ section }: { section: TileSection }) {
    const ctx = useHomeContext();
    const homePageState = ctx.homePageState;
    const setHomePageState = ctx.setHomePageState;

    const setFilterState = (filterState: Partial<HomePageFilterState>) => {
        setHomePageState({ ...homePageState, filterState: { ...(homePageState?.filterState ?? {}), ...filterState } });
    };

    if ((section.titleData?.length ?? 0) > 1) {
        return (
            <>
                {section.titleData!.map((f, i) => {
                    const isLast = i == section.titleData!.length - 1;
                    if (isLast) {
                        return f.name;
                    }
                    const id =
                        Number(f?.category_id ?? 0) > 0
                            ? `category_${f.category_id}`
                            : Number(f?.folder_id ?? 0) > 0
                              ? `folder_${f.folder_id}`
                              : undefined;
                    return (
                        <Fragment key={id}>
                            <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setFilterState({ folderSelected: id, lastFilterApplied: 'folderSelected' });
                                }}
                            >
                                {f.name}
                            </span>
                            {' / '}
                        </Fragment>
                    );
                })}
            </>
        );
    }
    return section.title;
}
